import {v4 as uuidv4} from 'uuid';
import {isResizableImage, resizeImage} from './imageResize';
import ApiCaller, {ApiEndpoints} from './ApiCaller';
import {getUrlWithEncodingAndAuth} from './urlHelpers';

/**
 * @registry Rewrites JWT-tokenized `<img>` src URLs in HTML to carry the current access token.
 */
export function fixRichTextTokens(html: string, accessToken?: string): string {
  const element = document.createElement('div');
  element.innerHTML = html as string;

  element.querySelectorAll('img').forEach(image => {
    if (image.src.indexOf('?jwt') > 0) {
      image.src = getUrlWithEncodingAndAuth(
        image.src.split('?jwt')[0],
        accessToken
      );
    }
  });

  return element.innerHTML;
}

/**
 * @registry Uploads an image file to S3 via a presigned URL and returns its authed access URL.
 */
export const uploadImage = async (
  image: File,
  accessToken?: string
): Promise<string | undefined> => {
  if (isResizableImage(image)) {
    image = await resizeImage(image);
  }

  try {
    const response = await ApiCaller({
      method: 'POST',
      endpointURL: ApiEndpoints.uploadFile,
      data: {filename: image.name, type: image.type},
    });

    const {uploadUrl, accessUrl} = await response.data;

    const uploadResponse = await ApiCaller({
      method: 'PUT',
      endpointURL: '',
      data: image,
      contentType: image.type,
      customEndpointURL: uploadUrl,
    });

    if (uploadResponse.status === 200) {
      const url = getUrlWithEncodingAndAuth(accessUrl, accessToken);
      return url;
    }
  } catch {
    throw 'An error occurred uploading image.';
  }
};

/**
 * @registry Fetches an `<img>` element's src URL and returns it as a `File`.
 */
export async function imageFileFromUrl(
  imageNode: HTMLImageElement
): Promise<File> {
  const url = imageNode.src;
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const fileName = uuidv4() + '.' + blob.type.split('/')[1];
    const file = new File([blob], fileName, {
      type: blob.type,
    });

    return file;
  } catch {
    throw 'An error occurred copying image.';
  }
}
